import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      // Restore user from sessionStorage cache only (no token handling — cookie is httpOnly)
      const savedUser = sessionStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });

  const [permissions, setPermissions] = useState(() => {
    try {
      const savedPerms = sessionStorage.getItem('permissions');
      return savedPerms ? JSON.parse(savedPerms) : null;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const navigate = useNavigate();

  const loadPermissions = async (userId) => {
    try {
      const response = await api.get(`/api/users/${userId}/permissions`);
      setPermissions(response.data);
      sessionStorage.setItem('permissions', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions({ isAdmin: false, permissions: {} });
      return { isAdmin: false, permissions: {} };
    }
  };

  const hasPermission = (module, permission = null) => {
    if (!permissions) return false;
    if (permissions.isAdmin) return true;

    const modulePerms = permissions.permissions?.[module];
    if (!modulePerms || modulePerms.length === 0) return false;
    if (permission === null) return true;

    if (module === 'inventory') {
      if (modulePerms.includes('approve')) return true;
      if (permission === 'submit' || permission === 'read_only') {
        return modulePerms.includes('submit') || modulePerms.includes('approve');
      }
      if (permission === 'read_only') {
        return modulePerms.includes('read_only') || modulePerms.includes('submit') || modulePerms.includes('approve');
      }
      return modulePerms.includes(permission);
    }

    return modulePerms.includes(permission) || modulePerms.includes('full_access');
  };

  const checkAuth = async (skipIfChecking = true) => {
    if (skipIfChecking && isCheckingAuth) return false;

    setIsCheckingAuth(true);

    try {
      const response = await api.get('/api/check-auth');

      if (response.data.authenticated && response.data.user) {
        setUser(response.data.user);
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.user.shipEngineAPI) {
          sessionStorage.setItem('shipEngineAPI', response.data.user.shipEngineAPI);
        }

        if (response.data.user.isPortalUser) {
          await loadPermissions(response.data.user.id);
        } else {
          setPermissions({ isAdmin: true, permissions: {} });
        }

        return true;
      }

      setUser(null);
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('shipEngineAPI');
      sessionStorage.removeItem('cart');
      sessionStorage.removeItem('lastNavigationRoute');
      return false;
    } catch (error) {
      if (error.response?.status === 401) {
        setUser(null);
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('permissions');
        sessionStorage.removeItem('shipEngineAPI');
        sessionStorage.removeItem('cart');
        sessionStorage.removeItem('lastNavigationRoute');

        if (window.location.pathname !== '/login') {
          setTimeout(() => navigate('/login'), 1000);
        }
      } else {
        console.error('Auth check error:', error);
      }

      return false;
    } finally {
      setLoading(false);
      setIsCheckingAuth(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await api.post('/api/login', credentials);

      if (response.data.user) {
        // Cookie set by server — no token handling needed here

        setUser(response.data.user);
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        if (response.data.user.shipEngineAPI) {
          sessionStorage.setItem('shipEngineAPI', response.data.user.shipEngineAPI);
        }

        if (response.data.user.isPortalUser) {
          await loadPermissions(response.data.user.id);
        } else {
          setPermissions({ isAdmin: true, permissions: {} });
        }

        const lastRoute = sessionStorage.getItem('lastNavigationRoute');
        let defaultRoute = '/dashboard';

        if (!lastRoute && !response.data.user.isAdmin) {
          if (hasPermission('transportation')) defaultRoute = '/bols';
          else if (hasPermission('inventory')) defaultRoute = '/inventory';
          else if (hasPermission('warehouse_receipts')) defaultRoute = '/warehouse-receipts';
          else if (hasPermission('invoices')) defaultRoute = '/invoices';
          else if (hasPermission('accounts')) defaultRoute = '/accounts';
          else if (hasPermission('ext_orders')) defaultRoute = '/ext-orders';
          else defaultRoute = '/profile';
        }

        navigate(lastRoute || defaultRoute);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      setUser(null);
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('shipEngineAPI');
      sessionStorage.removeItem('lastNavigationRoute');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/logout'); // clears httpOnly cookie server-side
    } catch { /* ignore network errors on logout */ }
    setUser(null);
    setPermissions(null);
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('permissions');
    sessionStorage.removeItem('shipEngineAPI');
    sessionStorage.removeItem('cart');
    sessionStorage.removeItem('lastNavigationRoute');
    navigate('/login');
  };

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;

    const performInitialAuthCheck = async () => {
      if (!mounted) return;

      const lastAuthCheck = sessionStorage.getItem('lastAuthCheck');
      if (lastAuthCheck && (Date.now() - parseInt(lastAuthCheck)) < 1000) {
        const savedUser = sessionStorage.getItem('user');
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            setUser(userData);
            const savedPerms = sessionStorage.getItem('permissions');
            if (!savedPerms && userData.isPortalUser) {
              await loadPermissions(userData.id);
            } else if (savedPerms) {
              setPermissions(JSON.parse(savedPerms));
            } else if (!userData.isPortalUser) {
              setPermissions({ isAdmin: true, permissions: {} });
            }
          } catch (error) {
            console.error('Error loading cached user data:', error);
          }
        }
        setLoading(false);
        return;
      }

      sessionStorage.setItem('lastAuthCheck', Date.now().toString());

      timeoutId = setTimeout(async () => {
        if (mounted) await checkAuth(false);
      }, 150);
    };

    performInitialAuthCheck();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (loading) return;

    const currentPath = window.location.pathname;

    if (!user && currentPath !== '/login' && !loading) {
      sessionStorage.removeItem('lastNavigationRoute');
      navigate('/login');
    } else if (user && (currentPath === '/' || currentPath.match(/^\/[^\/]+\/?$/))) {
      const lastRoute = sessionStorage.getItem('lastNavigationRoute');
      const validRoutes = ['/dashboard', '/bols', '/inventory', '/orders', '/ext-orders', '/accounts', '/invoices', '/billing', '/profile', '/catalog', '/warehouse-receipts', '/rate-calculator', '/users', '/pending-approvals', '/purchase-orders', '/returns'];

      if (validRoutes.includes(currentPath)) return;

      const targetRoute = (lastRoute && validRoutes.includes(lastRoute)) ? lastRoute : '/dashboard';
      navigate(targetRoute);
    }
  }, [user, loading, navigate]);

  return (
    <AuthContext.Provider value={{ user, permissions, loading, isCheckingAuth, login, logout, checkAuth, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthContext;
